/**
 * Trend weight: what the scale would say without the noise.
 *
 * Scale weight moves a kilo or more day to day on water, salt and glycogen, so
 * the raw number is useless for "am I losing". The trend is an exponentially
 * smoothed average (the Hacker's Diet 10% rule) made gap-aware: a weigh-in after
 * three missed days moves the trend as much as three daily ones would have
 * (α = 1 − 0.9^gap), rather than as much as one.
 *
 * Each step is winsorised to ±max(1.5 kg, 2%): a misread scale, or a weigh-in
 * typed in pounds into a kilogram box, nudges the trend instead of yanking it.
 */

import { daysBetween } from './days.ts'

export type DailyWeight = { day: string; kg: number }
export type TrendPoint = { day: string; kg: number; winsorised: number; trend: number }

const ALPHA_DAY = 0.1

/** Mean of each day's weigh-ins, oldest first. */
export function dailyWeights(rows: { day: string; kg: number }[]): DailyWeight[] {
  const by = new Map<string, { s: number; n: number }>()
  for (const r of rows) {
    if (!Number.isFinite(r.kg) || r.kg <= 0) continue
    const e = by.get(r.day) ?? { s: 0, n: 0 }
    e.s += r.kg; e.n++
    by.set(r.day, e)
  }
  return [...by.entries()]
    .map(([day, { s, n }]) => ({ day, kg: s / n }))
    .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0))
}

export function trendSeries(daily: DailyWeight[]): TrendPoint[] {
  const out: TrendPoint[] = []
  let prev: TrendPoint | null = null
  for (const d of daily) {
    if (!prev) {
      prev = { day: d.day, kg: d.kg, winsorised: d.kg, trend: d.kg }
      out.push(prev)
      continue
    }
    const gap = Math.max(1, daysBetween(prev.day, d.day))
    const a = 1 - Math.pow(1 - ALPHA_DAY, gap)
    const lim = Math.max(1.5, 0.02 * prev.trend)
    const delta = Math.max(-lim, Math.min(lim, d.kg - prev.trend))
    const p: TrendPoint = {
      day: d.day, kg: d.kg, winsorised: prev.trend + delta, trend: prev.trend + a * delta,
    }
    out.push(p)
    prev = p
  }
  return out
}

/** The trend as of `day`: the last point on or before it (held between). */
export function trendOn(series: TrendPoint[], day: string): number | null {
  let v: number | null = null
  for (const p of series) {
    if (p.day > day) break
    v = p.trend
  }
  return v
}

/** Ordinary least squares slope of y against x. */
export function olsSlope(xs: number[], ys: number[]): number | null {
  const n = xs.length
  if (n < 2) return null
  const mx = xs.reduce((a, b) => a + b, 0) / n
  const my = ys.reduce((a, b) => a + b, 0) / n
  let sxx = 0, sxy = 0
  for (let i = 0; i < n; i++) {
    sxx += (xs[i] - mx) ** 2
    sxy += (xs[i] - mx) * (ys[i] - my)
  }
  return sxx === 0 ? null : sxy / sxx
}

/** kg per week over the `window` days ending at `asOf`; null under 3 weigh-ins. */
export function weeklyRate(series: TrendPoint[], asOf: string, window = 21): number | null {
  const pts = series.filter((p) => p.day <= asOf && daysBetween(p.day, asOf) < window)
  if (pts.length < 3) return null
  const s = olsSlope(pts.map((p) => daysBetween(asOf, p.day)), pts.map((p) => p.winsorised))
  return s === null ? null : s * 7
}
